import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Check for existing session on mount
    useEffect(() => {
        const checkAuth = async () => {
            const token = localStorage.getItem('auth_token');
            const savedUser = localStorage.getItem('user');
            const rememberToken = localStorage.getItem('remember_token');
            
            if (token && savedUser) {
                try {
                    // Verify token is still valid
                    const response = await authAPI.getMe();
                    setUser(response.data);
                    localStorage.setItem('user', JSON.stringify(response.data));
                } catch (err) {
                    // Token invalid, try remember token
                    if (rememberToken) {
                        try {
                            const rememberResponse = await authAPI.verifyRememberToken(rememberToken);
                            const { access_token, user: userData } = rememberResponse.data;
                            localStorage.setItem('auth_token', access_token);
                            localStorage.setItem('user', JSON.stringify(userData));
                            setUser(userData);
                        } catch (rememberErr) {
                            // Remember token also invalid, clear everything
                            localStorage.removeItem('auth_token');
                            localStorage.removeItem('user');
                            localStorage.removeItem('remember_token');
                            setUser(null);
                        }
                    } else {
                        localStorage.removeItem('auth_token');
                        localStorage.removeItem('user');
                        setUser(null);
                    }
                }
            } else if (rememberToken) {
                // No current token but have remember token - try to restore session
                try {
                    const rememberResponse = await authAPI.verifyRememberToken(rememberToken);
                    const { access_token, user: userData } = rememberResponse.data;
                    localStorage.setItem('auth_token', access_token);
                    localStorage.setItem('user', JSON.stringify(userData));
                    setUser(userData);
                } catch (err) {
                    localStorage.removeItem('remember_token');
                    setUser(null);
                }
            }
            setLoading(false);
        };

        checkAuth();
    }, []);

    const login = useCallback(async (email, password) => {
        setError(null);
        setLoading(true);
        try {
            const response = await authAPI.login(email, password);
            const { access_token, user: userData } = response.data;
            
            localStorage.setItem('auth_token', access_token);
            localStorage.setItem('user', JSON.stringify(userData));
            setUser(userData);
            
            return { success: true, user: userData };
        } catch (err) {
            const message = err.response?.data?.detail || 'Invalid email or password';
            setError(message);
            return { success: false, error: message };
        } finally {
            setLoading(false);
        }
    }, []);

    const register = useCallback(async (email, password, firstName, lastName) => {
        setError(null);
        setLoading(true);
        try {
            const response = await authAPI.register(email, password, firstName, lastName);
            const { access_token, user: userData } = response.data;
            
            localStorage.setItem('auth_token', access_token);
            localStorage.setItem('user', JSON.stringify(userData));
            setUser(userData);
            
            return { success: true, user: userData };
        } catch (err) {
            const message = err.response?.data?.detail || 'Registration failed';
            setError(message);
            return { success: false, error: message };
        } finally {
            setLoading(false);
        }
    }, []);

    const requestMagicLink = useCallback(async (email) => {
        setError(null);
        try {
            await authAPI.requestMagicLink(email);
            return { success: true };
        } catch (err) {
            const message = err.response?.data?.detail || 'Failed to send magic link';
            setError(message);
            return { success: false, error: message };
        }
    }, []);

    const verifyMagicLink = useCallback(async (token) => {
        setError(null);
        setLoading(true);
        try {
            const response = await authAPI.verifyMagicLink(token);
            const { access_token, user: userData } = response.data;
            
            localStorage.setItem('auth_token', access_token);
            localStorage.setItem('user', JSON.stringify(userData));
            setUser(userData);
            
            return { success: true, user: userData };
        } catch (err) {
            const message = err.response?.data?.detail || 'Invalid or expired token';
            setError(message);
            return { success: false, error: message };
        } finally {
            setLoading(false);
        }
    }, []);

    const handleGoogleCallback = useCallback(async (sessionId) => {
        setError(null);
        setLoading(true);
        try {
            const response = await authAPI.googleCallback(sessionId);
            const { access_token, user: userData } = response.data;
            
            localStorage.setItem('auth_token', access_token);
            localStorage.setItem('user', JSON.stringify(userData));
            setUser(userData);
            
            return { success: true, user: userData };
        } catch (err) {
            const message = err.response?.data?.detail || 'Google authentication failed';
            setError(message);
            return { success: false, error: message };
        } finally {
            setLoading(false);
        }
    }, []);

    const handleAppleCallback = useCallback(async (idToken, code, userData) => {
        setError(null);
        setLoading(true);
        try {
            const response = await authAPI.appleCallback(idToken, code, userData);
            const { access_token, user: userData2 } = response.data;
            localStorage.setItem('auth_token', access_token);
            localStorage.setItem('user', JSON.stringify(userData2));
            setUser(userData2);
            return { success: true, user: userData2 };
        } catch (err) {
            const message = err.response?.data?.detail || 'Apple authentication failed';
            setError(message);
            return { success: false, error: message };
        } finally {
            setLoading(false);
        }
    }, []);

    const initiateGoogleLogin = useCallback(() => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + '/auth/callback';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }, []);

    const logout = useCallback(async () => {
        // Revoke remember token if exists
        const rememberToken = localStorage.getItem('remember_token');
        if (rememberToken) {
            try {
                await authAPI.revokeRememberToken(rememberToken);
            } catch (err) {
                // Ignore errors during logout
            }
        }
        
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        localStorage.removeItem('remember_token');
        setUser(null);
    }, []);

    const enableRememberDevice = useCallback(async () => {
        try {
            const response = await authAPI.createRememberToken();
            localStorage.setItem('remember_token', response.data.remember_token);
            return { success: true };
        } catch (err) {
            return { success: false, error: err.response?.data?.detail || 'Failed to enable remember device' };
        }
    }, []);

    const logoutAllDevices = useCallback(async () => {
        try {
            await authAPI.revokeAllRememberTokens();
            // Clear current session too
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            localStorage.removeItem('remember_token');
            setUser(null);
            return { success: true };
        } catch (err) {
            return { success: false, error: err.response?.data?.detail || 'Failed to logout from all devices' };
        }
    }, []);

    const refreshUser = useCallback(async () => {
        try {
            const response = await authAPI.getMe();
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
            return response.data;
        } catch (err) {
            console.error('Failed to refresh user:', err);
            return null;
        }
    }, []);

    const value = {
        user,
        loading,
        error,
        isAuthenticated: !!user,
        isAdmin: user?.is_admin || false,
        login,
        register,
        requestMagicLink,
        verifyMagicLink,
        handleGoogleCallback,
        initiateGoogleLogin,
        handleAppleCallback,
        logout,
        logoutAllDevices,
        enableRememberDevice,
        refreshUser,
        clearError: () => setError(null),
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
